import { Header, pxToRem, Stack, styled, Text } from '@lessonnine/design-system.lib';
import { push } from 'connected-react-router';
import isNumber from 'lodash/isNumber';
import PropTypes from 'prop-types';
import { stringify } from 'query-string';
import React from 'react';
// External Imports
import { connect } from 'react-redux';

import { siteInnerMaxWidth } from '../../../config/css/ui.mjs';
import { options } from '../../../config/reactRedux.mjs';
import { CONTENT_ARCHIVE } from '../../../config/types/prop.mjs';
import { ListViewContext } from '../../../context/listView.mjs';
import { createSelectTranslation } from '../../../selectors/createSelectTranslation.mjs';
import { selectActivePagination } from '../../../selectors/selectActivePagination.mjs';
import {
  selectSearchParameters,
  selectSearchPath,
  selectSearchResultsShowing,
} from '../../../selectors/selectSearch.mjs';
import { ContentListWithLoader } from '../../ContentList/ContentListWithLoader.jsx';
// Internal Imports
import { ButtonControl } from '../../FilterMenu/ButtonControl.jsx';
import { I18n } from '../../I18n/I18n.jsx';
import { Meta } from '../../Meta/Meta.jsx';
import { Pagination } from '../../Pagination/Pagination.jsx';
import { SearchField } from './SearchField.jsx';

// Local Variables
const StackWithMaxWidth = styled(Stack)`
  max-width: ${pxToRem(siteInnerMaxWidth)};
`;

const CenteredText = styled(Text)`
  text-align: center;
`;

// Component Definition
function SearchArchiveBase({
  getTranslation,
  items,
  onChangeFilter,
  pageMeta: {
    controls: { order },
  },
  pagination: { currentPage, hasMore, paginationLinks, totalPages },
  params,
  resultsShowing,
  search,
  total,
}) {
  const hasItems = items.length > 0;
  const hasResults = !!total && isNumber(total) && Boolean(params.s?.trim());
  const showPagination = hasMore || (currentPage === totalPages && totalPages > 1);

  const searchPageName = getTranslation('search', 'Search Articles');
  const magazineName = getTranslation('meta-title', 'Babbel Magazine');

  return (
    <StackWithMaxWidth gap={32}>
      <Meta title={`${searchPageName} - ${magazineName}`} />
      {/* eslint-disable-next-line react/jsx-pascal-case -- design-system's API */}
      <Header.h1>
        {params.s ? (
          <>
            <I18n defaultText="Search Results for" field="search-results-for" />
            {` "${params.s}"`}
          </>
        ) : (
          <I18n defaultText="Search" field="search" />
        )}
      </Header.h1>
      <SearchField onSubmit={search} params={params} />
      <Stack>
        {hasResults && (
          <Stack gap={32}>
            {!!total && resultsShowing.length > 0 && (
              <Text fontSize="big" fontWeight={700}>
                <I18n defaultText="Showing" field="showing_search1" />
                {` ${resultsShowing[0]}-${resultsShowing[1]} `}
                <I18n defaultText="of" field="of_search2" />
                {` ${total} `}
                <I18n defaultText="Results" field="results_search3" />
              </Text>
            )}
            <ButtonControl
              label={<I18n defaultText="Sort By" field="sort-by" />}
              name="order"
              onChange={onChangeFilter}
              options={order.options}
              selected={order.selected}
            />
            {hasItems && (
              <ListViewContext.Provider value="linear">
                <ContentListWithLoader items={items} loading={!hasItems} />
              </ListViewContext.Provider>
            )}
            {showPagination && (
              <Pagination
                currentPage={currentPage}
                links={paginationLinks}
                totalPages={totalPages}
              />
            )}
          </Stack>
        )}
        {!hasResults && (
          <Stack alignItems="center" gap={48} padding={[48, 0]}>
            {/* eslint-disable-next-line react/jsx-pascal-case -- design-system's API */}
            <Header.h2>
              <I18n defaultText="Uh Oh!" field="no-search-results1" />
            </Header.h2>
            <Stack as="p" gap={12}>
              <CenteredText fontSize="big">
                <I18n
                  defaultText="We can't seem to find any results for:"
                  field="no-search-results2"
                />
              </CenteredText>
              <CenteredText fontSize="big" fontWeight={700}>
                {params.s}
              </CenteredText>
            </Stack>
            <CenteredText fontSize="big">
              <I18n defaultText="Try searching for something else." field="no-search-results3" />
            </CenteredText>
          </Stack>
        )}
      </Stack>
    </StackWithMaxWidth>
  );
}

SearchArchiveBase.propTypes = {
  ...CONTENT_ARCHIVE,
  onChangeFilter: PropTypes.func.isRequired,
  params: PropTypes.shape({
    s: PropTypes.string,
  }).isRequired,
  resultsShowing: PropTypes.arrayOf(PropTypes.number).isRequired,
  search: PropTypes.func.isRequired,
  total: PropTypes.number.isRequired,
};

const mapStateToProps = (state) => {
  const { total } = selectActivePagination(state);
  return {
    getTranslation: createSelectTranslation(state),
    params: selectSearchParameters(state),
    resultsShowing: selectSearchResultsShowing(state),
    searchPath: selectSearchPath(state),
    total: total || 0,
  };
};

const mapDispatchToProps = (dispatch) => ({
  search: (searchPath) => (parameters) => {
    const queryString = stringify(parameters, { arrayFormat: 'bracket' });
    dispatch(push(`${searchPath}?${queryString}`));
  },
});

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...ownProps,
  search: dispatchProps.search(stateProps.searchPath),
});

const withRedux = connect(mapStateToProps, mapDispatchToProps, mergeProps, options);
const SearchArchive = withRedux(SearchArchiveBase);

// Module Exports
export { SearchArchive };
